/**
 * Rapid-fire block helpers (spec 29).
 *
 * A rapid-fire block is a timed section: the respondent answers as many of the block's questions
 * as they can before the clock runs out. Everything decision-shaped lives here — pure, no DOM, no
 * network — so the rules are testable on their own and SurveySession only carries the wiring.
 *
 * The clock is anchored SERVER-side (`ends_at` on the session), never on a local start time: a
 * reload has to resume on the real remaining time, and pausing the tab must not buy more.
 */
import type { SurveyBlockDefinition } from '../../utils/surveyApi';

/** Mirrors RAPID_FIRE_CONFIG['ALLOWED_QUESTION_TYPES'] in constants/interview_constants.py. */
export const RAPID_FIRE_ALLOWED_TYPES: ReadonlySet<string> = new Set([
  'single_choice', 'yes_no', 'rating_scale', 'likert_scale', 'nps', 'dropdown', 'numeric_input',
]);

export const RAPID_FIRE_DEFAULT_DURATION_SECONDS = 60;

/** The seconds-remaining threshold where the countdown switches to its urgent treatment. */
export const RAPID_FIRE_URGENT_SECONDS = 10;

export interface RapidFireConfig {
  enabled: boolean;
  duration_seconds: number;
  max_questions?: number | null;
  allow_skip: boolean;
  intro_text?: string;
}

export type RapidFireCloseReason = 'expired' | 'cap';

interface QuestionLike {
  id: string;
  type?: string;
  block_id?: string | null;
}

interface ResponseLike {
  question_id: string;
  state?: string;
}

/** The block's rapid-fire config, or null when that block is an ordinary one. */
export function rapidFireConfigFor(
  blocks: SurveyBlockDefinition[] | null | undefined,
  blockId: string | null | undefined,
): RapidFireConfig | null {
  if (!blocks || !blockId) return null;
  const block = blocks.find((b) => String(b.id) === String(blockId));
  const cfg = (block as { rapid_fire?: RapidFireConfig } | undefined)?.rapid_fire;
  return cfg && cfg.enabled ? cfg : null;
}

export function isRapidFireBlock(
  blocks: SurveyBlockDefinition[] | null | undefined,
  blockId: string | null | undefined,
): boolean {
  return rapidFireConfigFor(blocks, blockId) !== null;
}

/**
 * Where the block sits in the order the respondent actually sees. Display position, not array
 * index — under randomization the two differ, and the runner walks display positions.
 * Null when no question belongs to the block (empty or orphaned config).
 */
export function blockDisplayRange(
  questions: QuestionLike[],
  questionOrder: number[] | null | undefined,
  blockId: string,
): { start: number; end: number; size: number } | null {
  const order = questionOrder?.length ? questionOrder : questions.map((_, i) => i);
  const positions: number[] = [];
  order.forEach((qIdx, pos) => {
    const q = questions[qIdx];
    if (q && String(q.block_id ?? '') === String(blockId)) positions.push(pos);
  });
  if (!positions.length) return null;
  return { start: positions[0], end: positions[positions.length - 1], size: positions.length };
}

/**
 * Seconds left against the server deadline. Rounds UP so the respondent is never shown 0 while
 * they can still answer, and returns null (rather than guessing) when there is no usable
 * deadline — callers treat that as "keep going", so a failed clock read can't eat the section.
 */
export function remainingSeconds(
  endsAt: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!endsAt) return null;
  const end = new Date(endsAt).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - nowMs) / 1000));
}

/** How many questions in this block the respondent has actually answered (a skip doesn't count). */
export function answeredCountInBlock(
  questions: QuestionLike[],
  blockId: string,
  responses: ResponseLike[] | null | undefined,
): number {
  if (!responses?.length) return 0;
  const inBlock = new Set(
    questions.filter((q) => String(q.block_id ?? '') === String(blockId)).map((q) => String(q.id)),
  );
  return responses.filter(
    (r) => r.state === 'answered' && inBlock.has(String(r.question_id)),
  ).length;
}

/**
 * Whether the block is over, and why.
 *
 * The clock and the question cap are both just "this block is over", which is why they resolve to
 * one decision and one close call. Expiry wins a tie: the respondent was still looking at a
 * question, so "ran out of time" is the honest record.
 */
export function shouldCloseRapidFire(input: {
  remaining: number | null;
  answeredInBlock: number;
  blockSize: number;
  maxQuestions?: number | null;
}): { close: boolean; reason: RapidFireCloseReason | null } {
  const { remaining, answeredInBlock, blockSize, maxQuestions } = input;

  if (remaining !== null && remaining <= 0) return { close: true, reason: 'expired' };

  // A cap of null/0/negative means "no cap" — fail OPEN toward showing questions, never toward a
  // block that closes the instant it opens. Authoring rejects those values anyway; this is the
  // runtime's second line of defence for data already in the wild.
  const cap = typeof maxQuestions === 'number' && maxQuestions > 0
    ? Math.min(maxQuestions, blockSize)
    : blockSize;

  if (answeredInBlock >= cap) return { close: true, reason: 'cap' };
  return { close: false, reason: null };
}

/** The intro copy for a block, with the duration filled in. */
export function rapidFireIntroText(config: RapidFireConfig): string {
  const seconds = config.duration_seconds ?? RAPID_FIRE_DEFAULT_DURATION_SECONDS;
  if (config.intro_text?.trim()) return config.intro_text;
  return `You'll have ${seconds} seconds to answer as many questions as you can. `
    + 'Answer quickly — there are no right or wrong answers.';
}
